import { useCallback } from "react";
import { usePromptInjection } from "src/hooks/useToolActions";
import type { TrainingQuizQuestion } from "src/services/backend";
import { useStore } from "src/stores";

export type TrainingQuizAgentValues = {
  programId: string;
  documentId: string;
  documentVersionId: string;
  revision: number;
  isRevisionLive?: boolean;
  existingQuestions?: TrainingQuizQuestion[];
};

type TrainingQuizAgentInput =
  | TrainingQuizAgentValues
  | TrainingQuizAgentValues[];

export const buildTrainingQuizAgentPrompt = (input: TrainingQuizAgentInput) => {
  const quizzes = Array.isArray(input) ? input : [input];
  if (quizzes.length === 0) return "";
  const isEditing =
    quizzes.length === 1 && !!quizzes[0].existingQuestions?.length;
  const scope =
    quizzes.length === 1
      ? quizzes[0].isRevisionLive === false
        ? `for the current document draft for upcoming revision R${quizzes[0].revision + 1}`
        : `for revision R${quizzes[0].revision + 1}`
      : "for each training program below";
  const quizContext = quizzes
    .map(
      ({
        programId,
        documentId,
        documentVersionId,
        revision,
        isRevisionLive,
        existingQuestions,
      }) =>
        [
          `Training program ID: ${programId}\nDocument ID: ${documentId}\nExact document version ID: ${documentVersionId}\n${
            isRevisionLive === false
              ? `Current document draft for upcoming revision R${revision + 1}`
              : `Document revision R${revision + 1}`
          }`,
          existingQuestions?.length
            ? "A saved quiz exists for this exact document version."
            : "",
        ]
          .filter(Boolean)
          .join("\n")
    )
    .join("\n\n");

  return [
    isEditing
      ? `Open the saved multiple-choice training quiz ${scope}.`
      : `Create a four-question multiple-choice training quiz ${scope}.`,
    quizContext,
    isEditing
      ? "Call load_training_quiz first, then call read_document with the supplied Document ID and version_id set to the Exact document version ID. Compare the saved questions with that version's current content. Keep questions that are still accurate, and update questions or answers made stale by the new content. Use the saved quiz from backend context as the starting point; it is not embedded in this prompt."
      : "Call load_training_quiz first. Read each exact document version with read_document before writing questions. Give each question exactly four plausible answer options and one correct answer. Keep every question grounded only in its document content.",
    "Return one pending draft_training_quiz proposal for each supplied training program ID and exact document version ID. The card is temporary and must not change quiz data until the administrator explicitly chooses Save draft, Publish, or Overwrite Published Quiz.",
    ...(quizzes.some(({ isRevisionLive }) => isRevisionLive === false)
      ? [
          "A quiz prepared from a current document draft must remain unpublished until that revision is live; Save draft stores it as an unpublished quiz draft.",
        ]
      : []),
  ].join("\n\n");
};

export const useOpenTrainingQuizAgentPanel = () => {
  const { injectPrompt } = usePromptInjection();
  const { agentStore, uiStore } = useStore();

  return useCallback(
    (values: TrainingQuizAgentInput) => {
      const quizzes = Array.isArray(values) ? values : [values];
      const quizCount = quizzes.length;
      if (quizCount === 0) return;
      const isEditing =
        quizCount === 1 && !!quizzes[0].existingQuestions?.length;
      agentStore.navigateToThread(null);
      uiStore.setIsRightPanelOpen(true);
      injectPrompt({
        prompt: buildTrainingQuizAgentPrompt(values),
        preview: isEditing
          ? "Edit training quiz"
          : quizCount > 1
            ? `Draft ${quizCount} four-question training quizzes`
            : "Draft four-question training quiz",
        source: "draft-training-quiz",
        replaceInput: true,
        submit: isEditing,
      });
    },
    [agentStore, injectPrompt, uiStore]
  );
};
